/* CookieBanner — v1.14.78.0 (FASE 8.2)
 * GDPR-compliant cookie consent: 3 pulsanti equal-prominence,
 * categorie marketing/statistici OFF di default, blocco effettivo tracker
 * pre-consent, link "Modifica preferenze" sempre visibile in footer.
 */
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Shield, ChevronDown, ChevronUp, Check, X, ExternalLink } from "lucide-react";

const STORAGE_KEY = "colucci_privacy_consent";
// FASE 8.2 — bump consent version per refresh utente sul nuovo banner
// con categorie marketing/statistici granulari (era 2026.05.05).
const CONSENT_VERSION = "2026.06.26";

export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* noop */ }
  return null;
}

function saveConsent(consent) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...consent,
        version: CONSENT_VERSION,
        timestamp: new Date().toISOString(),
      })
    );
    // Notifica resto dell'app per attivare/disattivare tracker condizionali
    window.dispatchEvent(new CustomEvent("consent:changed", { detail: { ...consent, version: CONSENT_VERSION } }));
  } catch { /* noop */ }
}

// Helper esportato — l'app PUO' chiamarlo prima di caricare tracker JS.
// Ritorna true SOLO se l'utente ha esplicitamente acconsentito alla categoria.
export function hasConsentFor(category) {
  const c = getConsent();
  if (!c) return false;
  if (category === "tecnici" || category === "necessari") return true;  // strict-necessary sempre OK
  return Boolean(c[category]);
}

function shouldShowBannerInitial() {
  if (typeof window === "undefined") return false;
  const existing = getConsent();
  if (!existing) return true;
  if (existing.version !== CONSENT_VERSION) return true;
  return false;
}

function isOnAuthenticatedAdminRoute(pathname) {
  if (!pathname) return false;
  if (pathname === "/admin" || pathname === "/admin/") return false;
  return pathname.startsWith("/admin/");
}

const PRIMARY_BTN_CLASS =
  "flex-1 px-5 py-2.5 bg-[#163321] text-white text-sm font-semibold rounded-full hover:bg-[#1d4a2e] transition-all";

export default function CookieBanner() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(shouldShowBannerInitial);
  const [expanded, setExpanded] = useState(false);
  // FASE B (B4) — Traccia presenza del SiteAccessGate via MutationObserver
  // per nascondere il banner finché il gate è a schermo.
  const [gateOpen, setGateOpen] = useState(
    typeof document !== "undefined"
      && !!document.querySelector('[data-testid="site-access-gate"]')
  );

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const check = () => {
      const present = !!document.querySelector('[data-testid="site-access-gate"]');
      setGateOpen((prev) => (prev !== present ? present : prev));
    };
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true });
    // Fallback: check ogni 500ms per il primo secondo (edge case iOS)
    const iv = setInterval(check, 500);
    const stop = setTimeout(() => clearInterval(iv), 3000);
    return () => {
      obs.disconnect();
      clearInterval(iv);
      clearTimeout(stop);
    };
  }, []);
  // FASE 8.2: default categorie opzionali OFF (GDPR opt-in)
  const [consent, setConsent] = useState({
    tecnici: true,       // sempre on (necessari)
    stripe: false,       // pagamenti — opt-in
    statistici: false,   // analytics — opt-in
    marketing: false,    // marketing/profilazione — opt-in
  });

  useEffect(() => {
    if (visible && isOnAuthenticatedAdminRoute(pathname)) {
      saveConsent({ tecnici: true, stripe: false, statistici: false, marketing: false, choice: "auto_admin_strict_necessary" });
      setVisible(false);
      return;
    }
    if (visible) return;
    if (shouldShowBannerInitial()) {
      const t = setTimeout(() => setVisible(true), 200);
      return () => clearTimeout(t);
    }
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const acceptAll = () => {
    saveConsent({ tecnici: true, stripe: true, statistici: true, marketing: true, choice: "accept_all" });
    setVisible(false);
  };

  const rejectAll = () => {
    saveConsent({ tecnici: true, stripe: false, statistici: false, marketing: false, choice: "reject_all" });
    setVisible(false);
  };

  const acceptSelected = () => {
    saveConsent({ ...consent, choice: "custom" });
    setVisible(false);
  };

  if (!visible) return null;
  if (pathname.startsWith("/area-cliente")) return null;
  if (pathname === "/eventi" || pathname.startsWith("/eventi/")) return null;
  if (pathname.startsWith("/preventivo-")) return null;
  // FASE B (B4) — Nascondi cookie banner se il SiteAccessGate è ancora aperto
  // (altrimenti sovrappone il bottone "Entra" su schermi < 1080p).
  // Utilizza state reattivo aggiornato da MutationObserver.
  if (gateOpen) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[60] p-4 sm:p-6 animate-fade-in-up"
      data-testid="cookie-banner"
      role="dialog"
      aria-modal="false"
      aria-live="polite"
      aria-label="Informativa cookie e privacy"
    >
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl shadow-black/10 border border-zinc-200 overflow-hidden">
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#163321]/10 flex items-center justify-center shrink-0 mt-0.5">
              <Shield className="w-5 h-5 text-[#163321]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-base font-semibold text-zinc-900 mb-1">La tua privacy è importante</h3>
              <p className="text-sm text-zinc-500 leading-relaxed">
                Utilizziamo cookie <strong className="text-zinc-700">tecnici</strong> per il funzionamento del sito. Eventuali cookie statistici,
                marketing o di pagamento richiedono il tuo consenso esplicito.
                Puoi accettare tutto, rifiutare tutto o personalizzare la tua scelta in qualsiasi momento.
              </p>
            </div>
          </div>

          {/* Buttons — FASE 8.2: equal prominence GDPR-compliant */}
          <div className="flex flex-col sm:flex-row items-stretch gap-2 mt-5" data-testid="cookie-buttons">
            <button onClick={acceptAll}
              className={PRIMARY_BTN_CLASS}
              data-testid="cookie-accept-all">
              Accetta tutto
            </button>
            <button onClick={rejectAll}
              className={PRIMARY_BTN_CLASS}
              data-testid="cookie-reject-all">
              Rifiuta tutto
            </button>
            <button onClick={() => setExpanded(!expanded)}
              className={PRIMARY_BTN_CLASS}
              data-testid="cookie-customize">
              Personalizza {expanded ? <ChevronUp className="inline w-3.5 h-3.5 ml-1" /> : <ChevronDown className="inline w-3.5 h-3.5 ml-1" />}
            </button>
          </div>
        </div>

        {expanded && (
          <div className="border-t border-zinc-100 p-5 sm:p-6 bg-zinc-50/50 space-y-3" data-testid="cookie-details">
            <CategoryCard
              testid="cookie-category-tecnici"
              dotColor="bg-[#163321]"
              title="Cookie Tecnici"
              tag="Necessari"
              tagClass="bg-zinc-100 text-zinc-500"
              alwaysOn
              description="Necessari per il funzionamento del sito (sessione, autenticazione admin, sicurezza). Non possono essere disattivati. Nessun cookie viene impostato sui visitatori del sito pubblico fino a che non interagiscono con form o aree riservate."
            />
            <CategoryCard
              testid="cookie-category-stripe"
              dotColor="bg-[#D97B58]"
              title="Pagamenti (Stripe)"
              tag="Terze parti"
              tagClass="bg-[#D97B58]/10 text-[#D97B58]"
              checked={consent.stripe}
              onToggle={(v) => setConsent((p) => ({ ...p, stripe: v }))}
              description="Cookie impostati da Stripe durante il pagamento di consulenze. Attivati solo se procedi al pagamento. Necessari per elaborare pagamenti sicuri e prevenire frodi."
            />
            <CategoryCard
              testid="cookie-category-statistici"
              dotColor="bg-blue-500"
              title="Statistici (analytics)"
              tag="Anonimi"
              tagClass="bg-blue-50 text-blue-700"
              checked={consent.statistici}
              onToggle={(v) => setConsent((p) => ({ ...p, statistici: v }))}
              description="Aiutano a capire come gli utenti interagiscono con il sito (pagine viste, durata sessione). Dati anonimizzati e aggregati. Nessuna identificazione personale."
            />
            <CategoryCard
              testid="cookie-category-marketing"
              dotColor="bg-amber-500"
              title="Marketing & Profilazione"
              tag="Profilazione"
              tagClass="bg-amber-50 text-amber-700"
              checked={consent.marketing}
              onToggle={(v) => setConsent((p) => ({ ...p, marketing: v }))}
              description="Cookie usati per mostrare offerte personalizzate o pubblicità basata sui tuoi interessi. Attualmente NON utilizziamo cookie di profilazione attivi — questa categoria è disponibile per future integrazioni opt-in."
            />

            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-sm font-semibold text-zinc-900">Cosa NON utilizziamo (oggi)</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {["Google Analytics", "Facebook Pixel", "TikTok Pixel", "LinkedIn Insight", "Hotjar", "Remarketing Ads"].map((item) => (
                  <div key={item} className="flex items-center gap-1.5 text-xs text-zinc-500">
                    <X className="w-3 h-3 text-red-400" /><span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
              <div className="flex gap-4">
                <Link to="/privacy" className="text-xs text-[#163321] hover:underline flex items-center gap-1" onClick={() => setVisible(false)}>
                  Privacy Policy <ExternalLink className="w-3 h-3" />
                </Link>
                <Link to="/cookie-policy" className="text-xs text-[#163321] hover:underline flex items-center gap-1" onClick={() => setVisible(false)}>
                  Cookie Policy <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
              <button onClick={acceptSelected}
                className={PRIMARY_BTN_CLASS + " flex-none px-5"}
                data-testid="cookie-save-preferences">
                Salva preferenze
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryCard({ testid, dotColor, title, tag, tagClass, alwaysOn, checked, onToggle, description }) {
  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-2 h-2 rounded-full shrink-0 ${dotColor}`} />
          <span className="text-sm font-semibold text-zinc-900 truncate">{title}</span>
          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider shrink-0 ${tagClass}`}>{tag}</span>
        </div>
        {alwaysOn ? (
          <div className="flex items-center gap-1.5 text-xs text-[#163321] font-medium shrink-0">
            <Check className="w-3.5 h-3.5" /> Sempre attivi
          </div>
        ) : (
          <label className="relative inline-flex items-center cursor-pointer shrink-0">
            <input type="checkbox" checked={checked} onChange={(e) => onToggle(e.target.checked)} className="sr-only peer" />
            <div className="w-9 h-5 bg-zinc-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#163321]" />
          </label>
        )}
      </div>
      <p className="text-xs text-zinc-500 leading-relaxed">{description}</p>
    </div>
  );
}

/* Floating button — sempre visibile per "Modifica preferenze" in footer
 * Apre il banner di nuovo. */
export function PrivacySettingsButton() {
  const reopen = () => {
    localStorage.removeItem(STORAGE_KEY);
    window.location.reload();
  };

  return (
    <button onClick={reopen}
      className="fixed bottom-4 left-4 z-50 w-10 h-10 rounded-full bg-white border border-zinc-200 shadow-lg flex items-center justify-center hover:bg-zinc-50 hover:scale-105 transition-all group"
      title="Modifica preferenze cookie"
      aria-label="Modifica preferenze cookie"
      data-testid="privacy-settings-btn">
      <Shield className="w-4 h-4 text-zinc-400 group-hover:text-[#163321] transition-colors" />
    </button>
  );
}
